import { useCallback, useEffect, useState } from "react";
import { usePagination } from "../hooks/grupos_acesso_lista/pagination";
import { useFilters } from "../hooks/grupos_acesso_lista/filters";
import { GrupoAcesso } from "../../domain/models/grupo_acesso";
import { GrupoAcessoForm } from "../schemas/grupos_acesso_form";
import { useNavigate } from "react-router";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { processError } from "../../../../common/utils/base_error";
import { usePageLoader } from "../../../../common/contexts/page_content_context";

/**
 * useGrupoAcessoListaViewModel
 */

type LoadPageParams = {
  name?: string,
  description?: string,
  status?: number,
  page?: number,
  rowsPerPage?: number,
};

export const useGrupoAcessoListaViewModel = () => {
  const {
    administracao: {
      getGruposAcessoUseCase,
      deleteGrupoAcessoUseCase,
    },
  } = useDependencyInjection();

  const debouncedRef = useCreateDebounceHandler(750);
  const { pushErrorNotification, pushWarnNotification,
    pushInfoNotification, setPageLoading } = usePageLoader();

  const [loading, setLoading] = useState<boolean>(false);
  const [gruposAcessoLista, setGruposAcessoLista] = useState<GrupoAcesso[]>();
  const [dialogDeleteOpen, setDialogDeleteOpen] = useState(false);
  const [selectedAccessGroup, setSelectedAccessGroup] = useState<GrupoAcesso | undefined>();
  const [openDialog, setOpenDialog] = useState(false);

  const pagination = usePagination();

  const navigate = useNavigate();

  const filters = useFilters();

  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [internalErrorMessage, setInternalErrorMessage] = useState<string | undefined>();

  const loadPageData = useCallback(async (params?: LoadPageParams) => {
    try {
      setErrorMessage(undefined);

      if (params) {
        const computedFilterName = params.name ?? filters.name;
        const computedFilterDescription = params.description ?? filters.description;
        const computedFilterStatus = params.status ?? filters.status;
        const computedPage = params.page ?? pagination.page;
        const computedRowsPerPage = params.rowsPerPage ?? pagination.rowsPerPage;

        await getGruposAcessoUseCase.getGruposAcesso(
          computedPage,
          computedRowsPerPage,
          computedFilterName,
          computedFilterDescription,
          computedFilterStatus,
        );

        if (params.name !== undefined) filters.setNameFilter(params.name);
        if (params.description !== undefined) filters.setDescriptionFilter(params.description);
        if (params.status !== undefined) filters.setStatusFilter(params.status);
        if (params.page !== undefined) pagination.setPage(params.page);
        if (params.rowsPerPage !== undefined) pagination.setRowsPerPage(params.rowsPerPage);
      } else {
        await getGruposAcessoUseCase.getGruposAcesso(
          pagination.page,
          pagination.rowsPerPage,
          filters.name,
          filters.description,
          filters.status,
        );
      }

      setGruposAcessoLista(getGruposAcessoUseCase.items);

      pagination.setTotalItems(getGruposAcessoUseCase.itemsCount);
      pagination.setPageCount(getGruposAcessoUseCase.pagesCount);

    } catch (e: unknown) {
      pushErrorNotification('Error', processError(e, 'Falha ao buscar grupos de acesso!'));
      setErrorMessage("Falha ao carregar grupos de acesso!");
      setGruposAcessoLista([]);
    }
  }, [
    getGruposAcessoUseCase,
    filters,
    pagination,
  ]);

  useEffect(() => {
    (async function () {
      try {
        setLoading(true);
        if (!gruposAcessoLista || gruposAcessoLista.length === 0) {
          await loadPageData();
        }
      } catch (e: unknown) {

      } finally {
        setLoading(false);
      }
    })();
  }, [gruposAcessoLista, loadPageData]);

  const handleReload = useCallback(async () => {
    loadPageData();
  }, [
    loadPageData
  ]);

  const handleNewGroup = useCallback(() => {
    navigate("/GruposAcesso_cadastro");
  }, [navigate]);

  const handleNewGroupDialog = useCallback(() => {
    setSelectedAccessGroup(undefined);
    setOpenDialog(true);
  }, []);

  const handleNameFilterChange = useDebouncedCallback(debouncedRef, (nameFilter: string) => {
    filters.setNameFilter(nameFilter);
    return () => loadPageData({ name: nameFilter, page: 1 });
  }, [filters, loadPageData]);

  const handleDescriptionFilterChange = useDebouncedCallback(debouncedRef, (descriptionFilter: string) => {
    filters.setDescriptionFilter(descriptionFilter);
    return () => loadPageData({ description: descriptionFilter, page: 1 });
  }, [filters, loadPageData]);

  const handleStatusFilterChange = useDebouncedCallback(debouncedRef, (statusFilter: number) => {
    filters.setStatusFilter(statusFilter);
    return () => loadPageData({ status: statusFilter, page: 1 });
  }, [filters, loadPageData]);

  const handlePageChange = useCallback((page: number) => {
    loadPageData({ page: page + 1 });
  }, [loadPageData]);

  const handleRowsPerPageChange = useCallback((rowsPerPage: number) => {
    pagination.setRowsPerPage(rowsPerPage);
    loadPageData({ page: 1, rowsPerPage: rowsPerPage });
  }, [pagination, loadPageData]);

  const handleClearFilters = useCallback(() => {
    loadPageData({ name: "", description: "", status: 0, page: 1 });
  }, [loadPageData]);

  const handleDataGridActionEdit = useCallback((id: number | undefined) => {
    const GrupoAcesso = gruposAcessoLista?.find((x) => x.idGrupoAcesso === id);
    if (GrupoAcesso) {
      navigate("/GruposAcesso_cadastro", {
        state: {
          idGrupoAcesso: GrupoAcesso.idGrupoAcesso,
        }
      });
    }
  }, [gruposAcessoLista, navigate]);

  const handleDataGridActionDelete = useCallback((id: number | undefined) => {
    const GrupoAcesso = gruposAcessoLista?.find((x) => x.idGrupoAcesso === id);
    if (GrupoAcesso) {
      setSelectedAccessGroup(GrupoAcesso);
      setDialogDeleteOpen(true);
    }
  }, [gruposAcessoLista]);

  const handleConfirmDialog = useCallback(async (form: GrupoAcessoForm) => {
    setSelectedAccessGroup(undefined);
    setOpenDialog(false);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setOpenDialog(false);
  }, []);

  const handleDelete = useCallback(async () => {
    if (selectedAccessGroup) {
      try {
        setErrorMessage(undefined);

        await deleteGrupoAcessoUseCase.execute(selectedAccessGroup.idGrupoAcesso!);

        if (gruposAcessoLista && gruposAcessoLista?.length <= 1) {
          loadPageData({ page: 1 });
        } else {
          loadPageData();
        }
      } catch (e: unknown) {
        pushErrorNotification('Error', processError(e, 'Falha ao deletar grupo de acesso!'));

      } finally {
        setDialogDeleteOpen(false);
      };
    }
  }, [
    gruposAcessoLista,
    selectedAccessGroup,
    deleteGrupoAcessoUseCase,
    pushErrorNotification,
    loadPageData,
  ]);

  const handleSavedGrupoAcessoDialog = useCallback(async () => {
    await loadPageData();
    setOpenDialog(false);
  }, [loadPageData]);

  return {
    loading,
    gruposAcessoLista: gruposAcessoLista || [],
    selectedAccessGroup,
    openDialog,
    filters,
    pagination,
    errorMessage,
    dialogDeleteOpen,
    internalErrorMessage,

    setDialogDeleteOpen,
    setInternalErrorMessage,
    handleReload,
    handleNewGroup,
    handleNewGroupDialog,
    handlePageChange,
    handleRowsPerPageChange,
    handleNameFilterChange,
    handleDescriptionFilterChange,
    handleStatusFilterChange,
    handleClearFilters,
    handleDataGridActionEdit,
    handleDataGridActionDelete,
    handleConfirmDialog,
    handleDelete,
    handleCloseDialog,
    handleSavedGrupoAcessoDialog,
  };
}